import { useTranslation } from 'react-i18next';
import type { Params } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { useFlag } from '@innovamat/flags';
import { Grounder, HeadingSection } from '@innovamat/glimmer-components';
import { useTestsAndResultsQuery } from '@innovamat/glow-api-client';

import { useUser } from '../../../user-management';
import type { Status, TestType } from '../../modules/test/test';
import { EMPTY_CELL_VALUE } from '../../utils';
import { TestAndResultsProvider } from './components/test-and-results-drawer/context/test-and-results-provider';
import { TestsAndResultsTable } from './components/test-and-results-table/tests-and-results-table';
import { TypesOfTestsAnnouncementPanel } from './components/types-of-tests-announcement-panel/types-of-tests-announcement-panel';
import { testsAndResultsMockData } from './mock-fake-data';
import { TestsAndResultsEmptyState } from './tests-and-results.empty-state';
import { TestsAndResultsSkeleton } from './tests-and-results.skeleton';

const TestsAndResults = (): JSX.Element => {
  const { t } = useTranslation();

  const { classroomId } = useParams<Params>();
  const { user } = useUser();

  const { value: useMockData, loading: isFlagLoading } = useFlag(
    'schoolDemoMockReports',
    false,
    {
      identifier: '',
      custom: {
        schoolId: user?.organizationId || '',
        classroomId: classroomId || '',
      },
    }
  );

  const {
    data,
    isLoading: isLoadingTests,
    isError,
  } = useTestsAndResultsQuery(
    {
      classroomId: classroomId ?? '',
      academicYearId: user?.organization?.academicYearId ?? '',
    },
    {
      enabled:
        !!classroomId &&
        !!user?.organization?.academicYearId &&
        !isFlagLoading &&
        !useMockData,
    }
  );

  const testsAndResults = useMockData
    ? testsAndResultsMockData.testsAndResults
    : data?.testsAndResults ?? [];

  const testAndResultsRows = testsAndResults.map((test) => {
    const { test: testDetails, ...restTestProps } = test;

    return {
      ...restTestProps,
      id: test.classroomTestRoundId,
      rowName: t(`${test.roundName}`, EMPTY_CELL_VALUE),
      rowDescription: t(`${test.roundName}.desc`, ''),
      type: testDetails?.type as TestType,
      status: test.status as Status,
    };
  });

  const getViewContent = (): JSX.Element => {
    if (isLoadingTests || !testsAndResults || isError) {
      return <TestsAndResultsSkeleton />;
    }

    if (!testsAndResults.length) {
      return <TestsAndResultsEmptyState />;
    }

    return (
      <TestAndResultsProvider
        rows={testAndResultsRows}
        classroomId={classroomId ?? ''}
      >
        <TypesOfTestsAnnouncementPanel
          classroomId={classroomId}
          userId={user?.id}
        />
        <TestsAndResultsTable
          rows={testAndResultsRows}
          classroomId={classroomId ?? ''}
        />
      </TestAndResultsProvider>
    );
  };

  return (
    <>
      <HeadingSection title={t('testsAndResultsView.title')} />
      {getViewContent()}

      <Grounder />
    </>
  );
};

export { TestsAndResults };
